import { Component, computed, signal, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { catchError, finalize, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrganizationService, Organization } from '../../../../core/services/organization.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { UserOrganizationService } from '../../../../shared/services/user-organization.service';
import { AuthService } from '../../auth.service';

@Component({
  selector: 'app-join-organization',
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="border-t border-gray-200 pt-6 mt-6">
      <h3 class="text-lg font-medium text-gray-900 mb-4">---- alles ab hier ist nur testweise ----</h3>

      <!-- Sync Status Banner -->
      @if (syncStatus() === 'pending_relogin') {
        <div class="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-amber-800">Neue Organisationsmitgliedschaften erkannt</p>
              <p class="text-sm text-amber-700">Bitte aktualisieren Sie die Seite oder melden Sie sich erneut an, um alle Ihre Organisationen zu sehen.</p>
            </div>
          </div>
        </div>
      }

      <!-- Pending Organizations (newly joined, not yet in token) -->
      @if (pendingOrganizations().length > 0) {
        <div class="mb-6">
          <h4 class="text-sm font-medium text-gray-700 mb-2">Kürzlich beigetreten (Token-Aktualisierung ausstehend)</h4>
          <div class="space-y-2">
            @for (org of pendingOrganizations(); track org.organizationId) {
              <div class="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div class="flex items-center">
                  <div class="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                  <span class="text-sm font-medium text-blue-800">{{ org.organizationName }}</span>
                </div>
                <span class="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">AUSSTEHEND</span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Current Organizations -->
      <div class="mb-6">
        <h4 class="text-sm font-medium text-gray-700 mb-2">Aktuelle Organisationen</h4>
        @if (currentOrganizations().length > 0) {
          <div class="space-y-2">
            @for (org of currentOrganizations(); track org.id) {
              <div class="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div class="flex items-center">
                  <div class="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                  <span class="text-sm font-medium text-green-800">{{ org.name }}</span>
                </div>
                <span class="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">{{ getRolesForOrganization(org.id) }}</span>
              </div>
            }
          </div>
        } @else {
          <p class="text-sm text-gray-500 italic">Sie sind noch kein Mitglied einer Organisation.</p>
        }
      </div>

      <!-- Available Organizations -->
      <div>
        <h4 class="text-sm font-medium text-gray-700 mb-2">Verfügbare Organisationen</h4>
        @if (availableToJoin().length > 0) {
          <div class="space-y-2">
            @for (org of availableToJoin(); track org.id) {
              <div class="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                <div class="flex items-center">
                  <div class="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                  <span class="text-sm font-medium text-gray-700">{{ org.name }}</span>
                </div>
                <button
                  (click)="joinOrganization(org)"
                  [disabled]="isJoining() === org.id"
                  [class]="getJoinButtonClasses(org.id)"
                >
                  @if (isJoining() === org.id) {
                    <span class="flex items-center">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Beitreten...
                    </span>
                  } @else {
                    Beitreten
                  }
                </button>
              </div>
            }
          </div>
        } @else {
          <p class="text-sm text-gray-500 italic">
            @if (allOrganizations().length === 0) {
              Organisationen werden geladen...
            } @else {
              Sie sind bereits Mitglied aller verfügbaren Organisationen.
            }
          </p>
        }
      </div>

      <!-- Info Message -->
      <div class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-blue-700">
              <strong>Hinweis:</strong> Wenn Sie einer Organisation beitreten, erhalten Sie die USER-Rolle für diese Organisation.
              Sie können dann Buchungen vornehmen und auf Funktionen im Rahmen dieser Organisation zugreifen.
            </p>
          </div>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: []
})
export class JoinOrganizationComponent {
  private organizationService = inject(OrganizationService);
  private userOrganizationService = inject(UserOrganizationService);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  // Signals
  public allOrganizations = signal<Organization[]>([]);
  public isJoining = signal<string | null>(null);

  // Computed signals
  public currentOrganizations = this.organizationService.availableOrganizations;
  public syncStatus = this.userOrganizationService.syncStatus;
  public pendingOrganizations = this.userOrganizationService.pendingOrganizations;
  
  public availableToJoin = computed(() => {
    const all = this.allOrganizations();
    const current = this.currentOrganizations();
    const currentIds = new Set(current.map(org => org.id));
    
    return all.filter(org => !currentIds.has(org.id));
  });

  constructor() {
    this.loadAllOrganizations();
  }

  private loadAllOrganizations(): void {
    this.organizationService.getAllOrganizations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orgs) => {
          this.allOrganizations.set(orgs);
        },
        error: () => {
          this.toastService.error('Verfügbare Organisationen konnten nicht geladen werden');
        }
      });
  }

  joinOrganization(organization: Organization): void {
    if (this.isJoining()) {
      return; // Prevent multiple simultaneous requests
    }

    this.isJoining.set(organization.id);

    this.organizationService.joinOrganization(organization.id)
      .pipe(
        catchError((error) => {
          let errorMessage = 'Beitritt zur Organisation fehlgeschlagen';
          if (error.status === 409) {
            errorMessage = 'Sie sind bereits Mitglied dieser Organisation';
          } else if (error.status === 404) {
            errorMessage = 'Organisation nicht gefunden';
          } else if (error.status === 401) {
            errorMessage = 'Sie müssen angemeldet sein, um Organisationen beizutreten';
          }

          this.toastService.error(errorMessage);
          return of(null);
        }),
        finalize(() => {
          this.isJoining.set(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (result) => {
          if (result !== null) {
            // Only add to user organizations after successful join
            this.userOrganizationService.addOrganizationOptimistically(organization.id, organization.name);
            this.toastService.success(`Erfolgreich ${organization.name} beigetreten!`);
          }
        }
      });
  }

  getJoinButtonClasses(organizationId: string): string {
    const isCurrentlyJoining = this.isJoining() === organizationId;

    const baseClasses = 'btn btn-sm px-3 py-1 text-sm font-medium rounded-md transition-all duration-200';

    if (isCurrentlyJoining) {
      return `${baseClasses} btn-secondary cursor-not-allowed opacity-75`;
    }

    return `${baseClasses} btn-primary hover:shadow-md`;
  }

  getRolesForOrganization(orgId: string): string {
    const user = this.authService.currentUserSignal();
    if (!user?.userRoles) return 'MITGLIED';
    const roles = user.userRoles
      .filter(r => r.organizationId === orgId)
      .map(r => r.role);
    return roles.length > 0 ? roles.join(', ') : 'MITGLIED';
  }
}